// Global Instructions Rule Applied!

// Timing Constants
export const TIMING = {
  FOCUS_TIMEOUT: 100,
  FOCUS_RETRY_TIMEOUT: 150,
  FOCUS_FALLBACK_TIMEOUT: 500,
  DEBOUNCE_DELAY: 50,
  SCROLL_CHECK_DELAY: 100,
  RESIZE_DEBOUNCE: 50
} as const

/** Default corner for all react-toastify toasts */
export const TOAST_POSITION = 'bottom-right' as const

/**
 * Toast durations (ms). Values are half of the previous defaults.
 * Per-call `autoClose` overrides ToastContainer; update call sites when adding overrides.
 */
export const TOAST_AUTO_CLOSE = {
  /** Was 3000 — container default + standard errors */
  DEFAULT: 1500,
  /** Was 5000 — duplicate / validation emphasis */
  LONG: 2500,
  /** Was 2000 — brief success / info */
  SHORT: 1000
} as const

const TOAST_INTERACTION = {
  position: TOAST_POSITION,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true
} as const

// Toast Configuration
export const TOAST_CONFIG = {
  ...TOAST_INTERACTION,
  autoClose: TOAST_AUTO_CLOSE.LONG
} as const

export const TOAST_SUCCESS_CONFIG = {
  ...TOAST_INTERACTION,
  autoClose: TOAST_AUTO_CLOSE.DEFAULT
} as const

export const TOAST_INFO_CONFIG = {
  ...TOAST_INTERACTION,
  autoClose: TOAST_AUTO_CLOSE.SHORT
} as const

// UI Constants
export const UI = {
  ACTION_COLUMN_WIDTH: 60,
  BUTTON_SIZE: 'small' as const,
  TABLE_SIZE: 'small' as const,
  KEY_PREFIX: 'new_',
  MIN_HEIGHT: '16px',
  FONT_SIZE: '12px'
} as const

// CSS Classes
export const BUTTON_CLASSES = {
  SAVE: 'text-green-600 hover:text-green-700 hover:bg-green-50 p-0 h-5 w-5 min-w-0 flex items-center justify-center',
  CANCEL: 'text-red-600 hover:text-red-700 hover:bg-red-50 p-0 h-5 w-5 min-w-0 flex items-center justify-center',
  DELETE: 'delete-btn p-0 h-5 w-5 min-w-0 flex items-center justify-center',
  INLINE_EDIT: 'inline-edit-label cursor-pointer hover:bg-gray-50 px-2 py-0.5 rounded text-xs leading-tight',
  SCROLL_BUTTON: 'p-0 h-4 w-4 min-w-0 flex items-center justify-center text-gray-400 hover:text-gray-600',
  ELLIPSES_BUTTON: 'p-0 h-5 w-5 min-w-0 flex items-center justify-center text-gray-600 hover:text-gray-800'
} as const

// Text Classes
export const TEXT_CLASSES = {
  PLACEHOLDER: 'text-gray-400',
  SMALL: 'text-xs',
  SUCCESS: 'text-green-600',
  ERROR: 'text-red-600',
  BLUE: 'text-blue-600'
} as const

// Key Generation
export const generateNewKey = (): string => UI.KEY_PREFIX + crypto.randomUUID()
export const isNewEntry = (key: string | number | null | undefined): boolean => {
  if (!key) return false
  return String(key).startsWith(UI.KEY_PREFIX)
}
const isEditingNewEntry = (editingRowKey: string | number | null | undefined): boolean => {
  return editingRowKey !== null && editingRowKey !== undefined && isNewEntry(editingRowKey)
}

// Default Props
export const DEFAULT_PROPS = {
  mode: 'new' as const,
  size: UI.TABLE_SIZE,
  actionsWidth: UI.ACTION_COLUMN_WIDTH,
  addButtonTestId: 'add-button',
  deleteConfirmTitle: 'Delete Entry',
  deleteConfirmDescription: 'Are you sure you want to remove this entry?'
} as const
